import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Leaf, TreePine, HandHeart, Shield, ArrowRight, ExternalLink, Mail, Phone, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/n0vf802x_Plan%20de%20travail%2040.png";
const ASSOCIATION_IMAGE = "https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?crop=entropy&cs=srgb&fm=jpg&w=1200";
const BUREAU_IMAGE = "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/qtqnte09_image.png";

const bureauMembers = [
  { 
    name: "Agathe CARRIERE", 
    role: "Présidente", 
    initials: "AC",
    color: "from-fuchsia-500 to-purple-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/27pwoflt_image.png",
    photoStyle: { objectPosition: "30% 65%" },
    description: "Cheffe d'entreprise du Domaine de la Briouse & Cavalière"
  },
  { 
    name: "Eve LEDOUX-MICHEL", 
    role: "Vice-Présidente", 
    initials: "EL",
    color: "from-lime-500 to-green-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/9bzunubv_image.png",
    photoStyle: { objectPosition: "center 50%" },
    description: "Ostéopathe & Cavalière"
  },
  { 
    name: "Laura BEAUCHAMP", 
    role: "Trésorière", 
    initials: "LB",
    color: "from-fuchsia-500 to-purple-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/77iau311_image.png",
    photoStyle: { objectPosition: "center 20%" },
    description: "Comptable & Cavalière"
  },
  { 
    name: "Benjamin SIMON BAUMEL", 
    role: "Co-Trésorier", 
    initials: "BS",
    color: "from-lime-500 to-green-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/1t2g1cki_ben.jpg",
    photoStyle: { objectPosition: "center 20%" },
    description: "Négociant en pharmacie & Cavalier"
  },
  { 
    name: "Valérie LEDOUX-MICHEL", 
    role: "Secrétariat", 
    initials: "VL",
    color: "from-fuchsia-500 to-purple-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/c7h8ens0_image.png",
    photoStyle: { objectPosition: "center 30%" },
    description: "Éducatrice pour mineurs, Formatrice indépendante & Cavalière"
  },
  { 
    name: "Karine THOMAS", 
    role: "Secrétariat", 
    initials: "KT",
    color: "from-lime-500 to-green-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/ep7ke7du_Design%20sans%20titre%20%282%29.jpg",
    photoStyle: { objectPosition: "center 25%" },
    description: "Éleveuse & Propriétaire d'équidés"
  },
  { 
    name: "Emilie DEDEBAN", 
    role: "Responsable Achats", 
    initials: "ED",
    color: "from-fuchsia-500 to-purple-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/cxqg770z_emil.jpg",
    photoStyle: { objectPosition: "center 25%" },
    description: "Cadre au service du Développement Durable & Cavalière"
  },
  { 
    name: "Jérôme CARBONNEL", 
    role: "Communication", 
    initials: "JC",
    color: "from-lime-500 to-green-600",
    photo: "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/c0ky47j3_image.png",
    photoStyle: { objectPosition: "center 20%" },
    description: "Indépendant en Communication"
  },
];

// Values will be generated dynamically with translations
const getValues = (t) => [
  {
    icon: Heart,
    title: t('association.benevolence'),
    description: t('association.benevolenceDesc')
  },
  {
    icon: Shield,
    title: t('association.respect'),
    description: t('association.respectDesc')
  },
  {
    icon: Users,
    title: t('association.conviviality2'),
    description: t('association.convivialityDesc')
  },
  {
    icon: Leaf,
    title: t('association.environment'),
    description: t('association.environmentDesc')
  },
  {
    icon: HandHeart,
    title: t('association.transmission'),
    description: t('association.transmissionDesc')
  },
  {
    icon: TreePine,
    title: t('association.accessibility'),
    description: t('association.accessibilityDesc')
  }
];

const getKeyFigures = (t) => [
  { value: "5", label: t('association.eventsOrganized'), sublabel: t('association.inYear') },
  { value: "10", label: t('association.artistsPerformers'), sublabel: "" },
  { value: "80", label: t('association.ridersWelcomed'), sublabel: "" },
  { value: "600+", label: t('association.visitorsOnSite'), sublabel: "" }
];

// Actions are kept in French as they are specific to the association
const actions = [
  "Organisation de randonnées équestres",
  "Courses d'endurance (Championnat Départemental)",
  "Stages d'équitation avec des professionnels reconnus",
  "Concerts et soirées lyriques en plein air",
  "Brocantes et événements familiaux",
  "Ciné-goûters et projections",
  "Valorisation du site gallo-romain de Sanxay"
];

export default function Association() {
  const { t } = useTranslation();
  const values = getValues(t);
  const keyFigures = getKeyFigures(t);
  
  return (
    <div className="min-h-screen pt-24 bg-white" data-testid="association-page">
      {/* Header */}
      <section className="py-16 bg-gradient-to-br from-fuchsia-50 via-white to-lime-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('association.whoAreWe')}
            </span>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-zinc-900 mb-6">
              {t('association.title')}
            </h1>
            <p className="text-zinc-600 max-w-2xl mx-auto text-lg">
              {t('association.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Presentation */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                {t('association.heartOfMelusin')} <span className="text-fuchsia-600">{t('association.paysMelusin')}</span>
              </h2>
              <div className="space-y-4 text-zinc-600 leading-relaxed">
                <p>
                  <strong className="text-zinc-900">Brious'O Folies</strong> {t('association.presentation1')}
                </p>
                <p>
                  <strong className="text-zinc-900">{t('association.ourMission')}</strong> {t('association.missionDesc')}
                </p>
                <p>
                  {t('association.presentation2')} <strong className="text-zinc-900">{t('association.conviviality')}</strong>{t('association.presentation3')}
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/adhesion">
                  <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full px-8 py-4" data-testid="association-adhesion-btn">
                    {t('association.becomeMember')}
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/benevoles">
                  <Button variant="outline" className="border-2 border-lime-500 text-lime-600 hover:bg-lime-50 rounded-full px-8 py-4" data-testid="association-benevole-btn">
                    {t('association.becomeVolunteer')}
                  </Button>
                </Link>
                <Link to="/partenaires">
                  <Button variant="outline" className="border-2 border-fuchsia-400 text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-8 py-4" data-testid="association-partenaire-btn">
                    <Handshake className="mr-2 h-4 w-4" />
                    {t('association.becomePartner')}
                  </Button>
                </Link>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={ASSOCIATION_IMAGE}
                  alt="Cavaliers au Domaine de la Briouse"
                  className="w-full h-auto"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-fuchsia-400 rounded-full opacity-30 blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-lime-400 rounded-full opacity-30 blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Key Figures */}
      <section className="py-16 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-4">
              {t('association.keyFigures')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {keyFigures.map((figure, index) => (
              <div key={index} className="text-center" data-testid={`key-figure-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-fuchsia-400 mb-2">{figure.value}</p>
                <p className="text-white font-medium">{figure.label}</p>
                {figure.sublabel && <p className="text-zinc-400 text-sm">{figure.sublabel}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bureau Members */}
      <section className="py-20 bg-gradient-to-b from-white to-zinc-50" data-testid="bureau-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('association.ourTeam')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              {t('association.bureauMembers')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('association.bureauDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {bureauMembers.map((member, index) => (
              <Card
                key={index}
                className="group hover:-translate-y-2 transition-all duration-300 border-0 shadow-lg bg-white overflow-hidden"
                data-testid={`bureau-member-${index}`}
              >
                {member.photo ? (
                  <div className="relative h-56 overflow-hidden bg-zinc-100">
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      style={member.photoStyle || { objectPosition: 'center 25%' }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
                  </div>
                ) : (
                  <div className={`relative h-56 bg-gradient-to-br ${member.color} flex items-center justify-center`}>
                    <span className="text-5xl font-bold text-white/90">
                      {member.initials}
                    </span>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
                  </div>
                )}
                <CardContent className="p-5 pt-2">
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-fuchsia-100 text-fuchsia-600 rounded-full text-xs font-medium">
                      {member.role}
                    </span>
                  </div>
                  <h3 className="font-['Playfair_Display'] text-lg font-bold text-zinc-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">
                    {member.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('association.whatDrivesUs')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              {t('association.ourValues')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <Card
                key={index}
                className="group hover:-translate-y-2 transition-all duration-300 border border-zinc-100 hover:border-fuchsia-200 bg-white"
                data-testid={`value-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 mb-6 rounded-xl bg-gradient-to-br from-fuchsia-100 to-lime-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="h-7 w-7 text-fuchsia-600" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Actions */}
      <section className="py-20 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
                {t('association.ourImpact')}
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                {t('association.ourActions')}
              </h2>
              <p className="text-zinc-600 mb-8 leading-relaxed">
                {t('association.actionsDesc')}
              </p>
              <ul className="space-y-4">
                {actions.map((action, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-lime-500 mt-2 flex-shrink-0" />
                    <span className="text-zinc-700">{action}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={LOGO_URL}
                  alt="Logo Brious'O Folies"
                  className="w-64 h-auto drop-shadow-2xl"
                />
                <div className="absolute -z-10 inset-0 bg-gradient-to-br from-fuchsia-200 to-lime-200 blur-3xl scale-150 opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Card className="border-0 shadow-xl bg-gradient-to-r from-fuchsia-50 to-lime-50">
            <CardContent className="p-8 md:p-12">
              <div className="text-center mb-8">
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-zinc-900 mb-2">
                  {t('association.contactUs')}
                </h3>
                <p className="text-zinc-600">{t('association.contactQuestion')}</p>
              </div>
              <div className="flex flex-col md:flex-row gap-6 justify-center">
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <Mail className="h-5 w-5 text-fuchsia-600" />
                  <span className="text-zinc-700">{t('association.writeToUs')}</span>
                </Link>
                <a
                  href="tel:+33749513090"
                  className="flex items-center justify-center gap-3 px-6 py-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <Phone className="h-5 w-5 text-lime-600" />
                  <span className="text-zinc-700">07 49 51 30 90</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-fuchsia-600 to-fuchsia-700" data-testid="association-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center text-white">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-6">
            {t('association.joinAdventure')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('association.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/adhesion">
              <Button className="bg-white text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-10 py-6 text-lg font-medium" data-testid="cta-adhesion-btn">
                {t('association.membershipFor')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/benevoles">
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg" data-testid="cta-benevole-btn">
                {t('association.becomeVolunteer')}
              </Button>
            </Link>
            <Link to="/partenaires">
              <Button variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg" data-testid="cta-partenaire-btn">
                <Handshake className="mr-2 h-5 w-5" />
                {t('association.becomePartner')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
